import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  startAddSpecialistGuides,
  startDeleteSpecialistGuide,
  startDownloadSpecialistGuide,
  startLoadingSpecialistGuideOptions,
  startLoadingSpecialistGuides,
  startUpdateSpecialistGuide
} from '../../app/actions/specialistGuideActions';
import { sortRolesByDisplayOrder } from '../../app/helpers/specialistRoleModuleHelper';
import {
  formatGuideBytes,
  getGuideRoleLabel,
  guideMatchesModules,
  guideMatchesPhase,
  guideMatchesRole,
  listGuidePhases,
  toggleNumericId
} from '../../app/helpers/specialistGuideFilterHelper';
import { getGuideAccentTone, getGuideModuleTone, getGuideRoleTone } from '../../app/helpers/specialistGuideColorHelper';
import { GuideColorDot, SpecialistGuideMetaBadges } from '../specialist-guides/SpecialistGuideColorBadges';
import SpecialistGuideListFilters from '../specialist-guides/SpecialistGuideListFilters';
import '../specialist-guides/specialistGuides.css';

const emptyDraft = () => ({
  title: '',
  description: '',
  catalogrole_ids: [],
  catalogmodule_ids: [],
  applies_to_all_modules: true,
  guide_phase: '',
  enabled: true,
  files: [],
  fileItems: []
});

const toggleId = toggleNumericId;

const AdminSpecialistGuidesScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { guides, options, loading } = useSelector((state) => state.specialistGuide);
  const configuration = useSelector((state) => state.configuration);
  const titleEnabled = options.title_enabled === true
    || String(configuration?.specialistGuidesTitleEnabled || '').toLowerCase() === 'true';
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [filterRoleId, setFilterRoleId] = useState('');
  const [filterPhase, setFilterPhase] = useState('');
  const [filterModuleIds, setFilterModuleIds] = useState([]);

  useEffect(() => {
    dispatch(startLoadingSpecialistGuideOptions());
    dispatch(startLoadingSpecialistGuides());
  }, [dispatch]);

  const acceptAttr = useMemo(
    () => (options.extensions || []).map((ext) => `.${ext}`).join(','),
    [options.extensions]
  );

  const filteredGuides = useMemo(
    () => (guides || []).filter((row) => (
      guideMatchesRole(row, filterRoleId)
      && guideMatchesPhase(row, filterPhase)
      && guideMatchesModules(row, filterModuleIds)
    )),
    [guides, filterRoleId, filterPhase, filterModuleIds]
  );

  const startCreate = () => {
    setEditingId('new');
    setDraft(emptyDraft());
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setDraft({
      title: row.title || '',
      description: row.description || '',
      catalogrole_ids: (row.roles || []).map((item) => Number(item.catalogrole_id)),
      catalogmodule_ids: (row.modules || []).map((item) => Number(item.catalogmodule_id)),
      applies_to_all_modules: Boolean(row.applies_to_all_modules),
      guide_phase: row.guide_phase || '',
      enabled: row.enabled !== false,
      files: [],
      fileItems: []
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft());
  };

  const onFilesChange = (fileList) => {
    const files = Array.from(fileList || []);
    setDraft((prev) => ({
      ...prev,
      files,
      fileItems: files.map((file, index) => ({
        title: prev.fileItems[index]?.title || '',
        description: prev.fileItems[index]?.description || ''
      }))
    }));
  };

  const onSave = async () => {
    if (!draft.catalogrole_ids.length) {
      Swal.fire(t('titles.error'), t('specialistGuides.needRoles'), 'warning');
      return;
    }
    if (!draft.guide_phase) {
      Swal.fire(t('titles.error'), t('specialistGuides.needPhase'), 'warning');
      return;
    }
    if (!draft.applies_to_all_modules && !draft.catalogmodule_ids.length) {
      Swal.fire(t('titles.error'), t('specialistGuides.needModules'), 'warning');
      return;
    }

    if (editingId === 'new') {
      if (!draft.files.length) {
        Swal.fire(t('titles.error'), t('specialistGuides.needFiles'), 'warning');
        return;
      }
      const missing = draft.fileItems.some((item) => !String(item.description || '').trim());
      if (missing) {
        Swal.fire(t('titles.error'), t('specialistGuides.needDescription'), 'warning');
        return;
      }
      const formData = new FormData();
      draft.files.forEach((file) => formData.append('files', file));
      formData.append('meta', JSON.stringify({
        applies_to_all_modules: draft.applies_to_all_modules,
        catalogrole_ids: draft.catalogrole_ids,
        catalogmodule_ids: draft.applies_to_all_modules ? [] : draft.catalogmodule_ids,
        guide_phase: draft.guide_phase,
        items: draft.fileItems.map((item) => ({
          title: titleEnabled ? item.title : '',
          description: item.description
        }))
      }));
      const body = await dispatch(startAddSpecialistGuides(formData));
      if (body && body.ok) cancelEdit();
      return;
    }

    if (!String(draft.description || '').trim()) {
      Swal.fire(t('titles.error'), t('specialistGuides.needDescription'), 'warning');
      return;
    }
    const formData = new FormData();
    if (draft.files[0]) {
      formData.append('file', draft.files[0]);
    }
    formData.append('meta', JSON.stringify({
      title: titleEnabled ? draft.title : '',
      description: draft.description,
      applies_to_all_modules: draft.applies_to_all_modules,
      catalogrole_ids: draft.catalogrole_ids,
      catalogmodule_ids: draft.applies_to_all_modules ? [] : draft.catalogmodule_ids,
      guide_phase: draft.guide_phase,
      enabled: draft.enabled
    }));
    const body = await dispatch(startUpdateSpecialistGuide(editingId, formData));
    if (body && body.ok) cancelEdit();
  };

  const onDelete = async (row) => {
    const result = await Swal.fire({
      title: t('specialistGuides.confirmDelete'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('buttons.delete', { defaultValue: 'Eliminar' }),
      cancelButtonText: t('buttons.cancel', { defaultValue: 'Cancelar' })
    });
    if (result.isConfirmed) {
      dispatch(startDeleteSpecialistGuide(row.id));
    }
  };

  return (
    <div className="container mt-4 mb-5">
      <h3>{t('specialistGuides.adminTitle')}</h3>
      <p className="text-muted">{t('specialistGuides.adminSubtitle', { maxMb: options.max_mb || 20 })}</p>
      <button type="button" className="btn btn-dark mb-3" onClick={startCreate}>
        {t('specialistGuides.add')}
      </button>

      {editingId ? (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">{editingId === 'new' ? t('specialistGuides.add') : t('specialistGuides.edit')}</h5>
            <div className="row g-3">
              {editingId === 'new' ? (
                <div className="col-12">
                  <label className="form-label">{t('specialistGuides.fields.files')}</label>
                  <input
                    className="form-control"
                    type="file"
                    multiple
                    accept={acceptAttr}
                    onChange={(event) => onFilesChange(event.target.files)}
                  />
                </div>
              ) : (
                <div className="col-12">
                  <label className="form-label">{t('specialistGuides.fields.replaceFile')}</label>
                  <input
                    className="form-control"
                    type="file"
                    accept={acceptAttr}
                    onChange={(event) => onFilesChange(event.target.files)}
                  />
                </div>
              )}

              {editingId === 'new' && draft.files.map((file, index) => (
                <div className="col-12" key={`${file.name}-${index}`}>
                  <div className="border rounded p-3">
                    <strong>{file.name}</strong>
                    <span className="text-muted ms-2">{formatGuideBytes(file.size)}</span>
                    {titleEnabled ? (
                      <input
                        className="form-control mt-2"
                        placeholder={t('specialistGuides.fields.title')}
                        value={draft.fileItems[index]?.title || ''}
                        onChange={(event) => {
                          const next = [...draft.fileItems];
                          next[index] = { ...next[index], title: event.target.value };
                          setDraft({ ...draft, fileItems: next });
                        }}
                      />
                    ) : null}
                    <textarea
                      className="form-control mt-2"
                      rows={2}
                      placeholder={t('specialistGuides.fields.description')}
                      value={draft.fileItems[index]?.description || ''}
                      onChange={(event) => {
                        const next = [...draft.fileItems];
                        next[index] = { ...next[index], description: event.target.value };
                        setDraft({ ...draft, fileItems: next });
                      }}
                    />
                  </div>
                </div>
              ))}

              {editingId !== 'new' ? (
                <>
                  {titleEnabled ? (
                    <div className="col-md-6">
                      <label className="form-label">{t('specialistGuides.fields.title')}</label>
                      <input className="form-control" value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} />
                    </div>
                  ) : null}
                  <div className="col-12">
                    <label className="form-label">{t('specialistGuides.fields.description')}</label>
                    <textarea className="form-control" rows={3} value={draft.description} onChange={(event) => setDraft({ ...draft, description: event.target.value })} />
                  </div>
                  <div className="col-12">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="guide-enabled"
                        checked={!!draft.enabled}
                        onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
                      />
                      <label className="form-check-label" htmlFor="guide-enabled">{t('specialistGuides.fields.enabled')}</label>
                    </div>
                  </div>
                </>
              ) : null}

              <div className="col-md-6">
                <label className="form-label" htmlFor="guide-phase">{t('specialistGuides.fields.phase')}</label>
                <select
                  id="guide-phase"
                  className="form-select"
                  value={draft.guide_phase || ''}
                  onChange={(event) => setDraft({ ...draft, guide_phase: event.target.value })}
                >
                  <option value="">{t('specialistGuides.filters.selectPhase')}</option>
                  {listGuidePhases().map((phase) => (
                    <option key={phase.value} value={phase.value}>{phase.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <label className="form-label">{t('specialistGuides.fields.roles')}</label>
                <div className="d-flex flex-wrap gap-3">
                  {(options.roles || []).map((role) => (
                    <div className="form-check" key={role.id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`guide-role-${role.id}`}
                        checked={draft.catalogrole_ids.includes(Number(role.id))}
                        onChange={() => setDraft({ ...draft, catalogrole_ids: toggleId(draft.catalogrole_ids, role.id) })}
                      />
                      <label className="form-check-label" htmlFor={`guide-role-${role.id}`}>
                        <GuideColorDot tone={getGuideRoleTone(role)} />
                        {getGuideRoleLabel(role, t)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="col-12">
                <div className="form-check mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="guide-all-modules"
                    checked={!!draft.applies_to_all_modules}
                    onChange={(event) => setDraft({ ...draft, applies_to_all_modules: event.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="guide-all-modules">{t('specialistGuides.fields.allModules')}</label>
                </div>
                {!draft.applies_to_all_modules ? (
                  <div className="d-flex flex-wrap gap-3">
                    {(options.modules || []).map((moduleItem) => (
                      <div className="form-check" key={moduleItem.id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`guide-mod-${moduleItem.id}`}
                          checked={draft.catalogmodule_ids.includes(Number(moduleItem.id))}
                          onChange={() => setDraft({ ...draft, catalogmodule_ids: toggleId(draft.catalogmodule_ids, moduleItem.id) })}
                        />
                        <label className="form-check-label" htmlFor={`guide-mod-${moduleItem.id}`}>
                          <GuideColorDot tone={getGuideModuleTone(moduleItem)} />
                          {moduleItem.name}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            <div className="mt-3 d-flex gap-2">
              <button type="button" className="btn btn-dark" onClick={onSave}>{t('buttons.save', { defaultValue: 'Guardar' })}</button>
              <button type="button" className="btn btn-outline-secondary" onClick={cancelEdit}>{t('buttons.cancel', { defaultValue: 'Cancelar' })}</button>
            </div>
          </div>
        </div>
      ) : null}

      {loading ? <p>{t('specialistGuides.loading')}</p> : null}

      <SpecialistGuideListFilters
        t={t}
        roles={sortRolesByDisplayOrder(options.roles || [])}
        modules={options.modules || []}
        filterRoleId={filterRoleId}
        filterPhase={filterPhase}
        filterModuleIds={filterModuleIds}
        showRoleFilter
        visibleCount={filteredGuides.length}
        onRoleChange={setFilterRoleId}
        onPhaseChange={setFilterPhase}
        onModulesChange={setFilterModuleIds}
        onClear={() => {
          setFilterRoleId('');
          setFilterPhase('');
          setFilterModuleIds([]);
        }}
      />

      <div className="table-responsive">
        <table className="table table-sm align-middle">
          <thead>
            <tr>
              <th>{t('specialistGuides.fields.description')}</th>
              <th>{t('specialistGuides.fields.file')}</th>
              <th>{t('specialistGuides.fields.roles')}</th>
              <th>{t('specialistGuides.fields.phase')}</th>
              <th>{t('specialistGuides.fields.modules')}</th>
              <th>{t('specialistGuides.fields.size')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredGuides.map((row) => (
              <tr key={row.id} className="sg-row-accent" style={{ '--sg-accent': getGuideAccentTone(row).accent }}>
                <td>
                  {titleEnabled && row.title ? <div><strong>{row.title}</strong></div> : null}
                  <div>{row.description}</div>
                  {row.enabled === false ? <span className="badge bg-secondary">{t('specialistGuides.disabled')}</span> : null}
                </td>
                <td>{row.original_filename}</td>
                <td>
                  <SpecialistGuideMetaBadges roles={row.roles} t={t} showPhase={false} showModules={false} />
                </td>
                <td>
                  <SpecialistGuideMetaBadges phase={row.guide_phase} t={t} showRoles={false} showModules={false} />
                </td>
                <td>
                  <SpecialistGuideMetaBadges
                    modules={row.modules}
                    appliesToAll={row.applies_to_all_modules}
                    t={t}
                    showRoles={false}
                    showPhase={false}
                  />
                </td>
                <td>{formatGuideBytes(row.size_bytes)}</td>
                <td className="text-nowrap">
                  <button type="button" className="btn btn-sm btn-outline-dark me-1" onClick={() => startEdit(row)}>{t('buttons.edit', { defaultValue: 'Editar' })}</button>
                  <button type="button" className="btn btn-sm btn-outline-secondary me-1" onClick={() => dispatch(startDownloadSpecialistGuide(row.id, row.original_filename))}>{t('specialistGuides.download')}</button>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onDelete(row)}>{t('buttons.delete', { defaultValue: 'Eliminar' })}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loading && (guides || []).length === 0 ? (
        <p className="text-muted">{t('specialistGuides.emptyAdmin')}</p>
      ) : null}
      {!loading && (guides || []).length > 0 && filteredGuides.length === 0 ? (
        <p className="text-muted">{t('specialistGuides.filters.empty')}</p>
      ) : null}
    </div>
  );
};

export default AdminSpecialistGuidesScreen;
